/**
 * Derives the draft context from whatever composer body is currently loaded.
 *
 * Each context owns its own draft: a reply draft in discussion X must never
 * surface in discussion Y, and a new-discussion draft is separate from both.
 *
 * The shape of `attrs` is what distinguishes the three cases, rather than the
 * component class: subclasses introduced by other extensions still carry the
 * same attributes, and matching on them keeps this working for those too.
 */
export default function contextFor(composerBody) {
  if (!composerBody || !composerBody.attrs) return null;

  const { post, discussion } = composerBody.attrs;

  // Editing an existing post: keyed by the post itself.
  if (post && typeof post.id === 'function' && post.id()) {
    return { type: 'edit', postId: post.id(), discussionId: null };
  }

  // Replying: keyed by the discussion being replied to.
  if (discussion && typeof discussion.id === 'function' && discussion.id()) {
    return { type: 'reply', discussionId: discussion.id(), postId: null };
  }

  // Starting a new discussion: neither an existing post nor a discussion is
  // attached. `DiscussionComposer` only wires up `composer.fields.title` in
  // its own `oninit`, which runs after this one, so that field cannot be used
  // to detect this case — the absence of the other two contexts can.
  return { type: 'discussion', discussionId: null, postId: null };
}

/**
 * Query parameters identifying a context, for GET and DELETE requests.
 */
export function contextParams(context) {
  const params = { type: context.type };

  if (context.discussionId) params.discussionId = context.discussionId;
  if (context.postId) params.postId = context.postId;

  return params;
}

/**
 * A new-discussion draft carries a title; the others do not.
 */
export function hasTitle(context) {
  return context.type === 'discussion';
}
